/** App-wide state: backend, session, the open site (config, content service, caches, publish tracking), toasts. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Content, loadConfig, type CommitInfo, type EntrySummary, type PendingChanges, type PublishStatus, type SiteConfig } from '@hotpot/core';
import type { Backend, Me, SiteHandle } from './backend/types.js';

export const BackendContext = createContext<Backend>(null!);
export const useBackend = () => useContext(BackendContext);

// ------------------------------------------------------------------ session

interface Session {
  me: Me | null;
  setMe: (me: Me | null) => void;
  refresh: () => Promise<void>;
}
export const SessionContext = createContext<Session>(null!);
export const useSession = () => useContext(SessionContext);

// ------------------------------------------------------------------ toasts

export interface Toast {
  id: number;
  text: ReactNode;
  action?: { label: string; onClick: () => void };
}
const ToastContext = createContext<(text: ReactNode, action?: Toast['action']) => void>(() => {});
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(0);
  const push = useCallback((text: ReactNode, action?: Toast['action']) => {
    const id = ++next.current;
    setToasts((t) => [...t.slice(-2), { id, text, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div className="toast" key={t.id}>
            <span>{t.text}</span>
            {t.action && (
              <button className="link-btn ink" onClick={t.action.onClick}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ------------------------------------------------------------------ site

export interface Publish {
  sha: string;
  message: string;
  status: PublishStatus;
  url?: string;
}

export interface SiteState {
  handle: SiteHandle;
  config: SiteConfig;
  content: Content;
  version: number;
  list: (collection: string) => Promise<EntrySummary[]>;
  /** Call after every save. On a site with drafts the commit is a draft (its build is the preview). */
  afterCommit: (commit: CommitInfo, url?: string) => void;
  /** The latest live build being followed. */
  publish: Publish | null;
  /** The latest preview build being followed (sites with drafts). */
  preview: Publish | null;
  /** Unpublished changes (sites with drafts); null while loading or without drafts. */
  pending: PendingChanges | null;
  /** Publish every pending change; returns false when there was nothing to publish. */
  publishAll: (url?: string) => Promise<boolean>;
  discardAll: () => Promise<void>;
  author: { name: string; email: string };
}

const SiteContext = createContext<SiteState | null>(null);
export const useSite = () => {
  const s = useContext(SiteContext);
  if (!s) throw new Error('useSite outside a site');
  return s;
};
export const useMaybeSite = () => useContext(SiteContext);

export function SiteProvider({ handle, children }: { handle: SiteHandle; children: ReactNode }) {
  const { me } = useSession();
  const config = useMemo(() => loadConfig(handle.configText), [handle.configText]);
  const content = useMemo(() => new Content(handle.storage, config), [handle.storage, config]);
  const [version, setVersion] = useState(0);
  const [publish, setPublish] = useState<Publish | null>(null);
  const [preview, setPreview] = useState<Publish | null>(null);
  const [pending, setPending] = useState<PendingChanges | null>(null);
  const drafts = handle.drafts;
  const cache = useRef(new Map<string, { version: number; promise: Promise<EntrySummary[]> }>());

  const list = useCallback(
    (collection: string) => {
      const hit = cache.current.get(collection);
      if (hit && hit.version === version) return hit.promise;
      const promise = content.listEntries(collection);
      promise.catch(() => cache.current.delete(collection));
      cache.current.set(collection, { version, promise });
      return promise;
    },
    [content, version],
  );

  const afterCommit = useCallback(
    (commit: CommitInfo, url?: string) => {
      setVersion((v) => v + 1);
      const p = { sha: commit.sha, message: commit.message, status: { state: 'pending' as const }, url };
      if (drafts) setPreview(p);
      else setPublish(p);
    },
    [drafts],
  );

  // Unpublished changes, refreshed after every save.
  useEffect(() => {
    if (!drafts) return;
    let alive = true;
    drafts.pending().then(
      (p) => alive && setPending(p),
      () => alive && setPending(null),
    );
    return () => {
      alive = false;
    };
  }, [drafts, version]);

  const publishAll = useCallback(
    async (url?: string) => {
      if (!drafts) return false;
      const commit = await drafts.publish();
      setVersion((v) => v + 1);
      if (!commit) return false;
      setPublish({ sha: commit.sha, message: commit.message, status: { state: 'pending' }, url: url ?? (handle.site.url || undefined) });
      return true;
    },
    [drafts, handle.site.url],
  );

  const discardAll = useCallback(async () => {
    if (!drafts) return;
    await drafts.discard();
    setPreview(null);
    setVersion((v) => v + 1);
  }, [drafts]);

  // Follow the builds of the latest save / publish until they are live or failed.
  useBuild(publish, setPublish, handle.storage);
  useBuild(preview, setPreview, handle.storage);

  const value = useMemo<SiteState>(
    () => ({
      handle,
      config,
      content,
      version,
      list,
      afterCommit,
      publish,
      preview,
      pending,
      publishAll,
      discardAll,
      author: { name: me?.user.name || me?.user.email || '', email: me?.user.email ?? '' },
    }),
    [handle, config, content, version, list, afterCommit, publish, preview, pending, publishAll, discardAll, me],
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

function useBuild(build: Publish | null, set: (fn: (p: Publish | null) => Publish | null) => void, storage: SiteHandle['storage']) {
  useEffect(() => {
    if (!build || build.status.state === 'live' || build.status.state === 'failed') return;
    let stopped = false;
    const started = Date.now();
    const tick = async () => {
      if (stopped) return;
      try {
        const status = await storage.publishStatus(build.sha);
        if (stopped) return;
        set((p) => (p && p.sha === build.sha ? { ...p, status } : p));
        if (status.state === 'live' || status.state === 'failed') return;
      } catch {
        /* keep trying */
      }
      if (Date.now() - started < 15 * 60_000) timer = setTimeout(tick, 2500);
    };
    let timer = setTimeout(tick, 1200);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build?.sha, build?.status.state, storage]);
}

// ------------------------------------------------------------------ async helper

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: Error | undefined; loading: boolean; reload: () => void } {
  const [state, setState] = useState<{ data?: T; error?: Error; loading: boolean }>({ loading: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    fn().then(
      (data) => alive && setState({ data, loading: false }),
      (error) => alive && setState({ error, loading: false }),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { data: state.data, error: state.error, loading: state.loading, reload: () => setN((x) => x + 1) };
}
